import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import { LockOutlined, StarOutline } from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';

interface PremiumFeatureGuardProps {
  children: React.ReactNode;
  featureName?: string; // Nom de la fonctionnalité pour personnaliser le message
}

const PremiumFeatureGuard: React.FC<PremiumFeatureGuardProps> = ({
  children,
  featureName = 'cette fonctionnalité',
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const hasPremiumAccess = user?.subscriptionTier === 'PREMIUM';

  // Si l'utilisateur a l'accès Premium, afficher le contenu
  if (hasPremiumAccess) {
    return <>{children}</>;
  }

  // Sinon, afficher un message invitant à passer à Premium
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: 'center',
        background: 'linear-gradient(145deg, #FEF3C7 0%, #FDE68A 100%)',
        border: '2px solid #F59E0B',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#F59E0B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockOutlined sx={{ fontSize: 48, color: 'white' }} />
        </Box>
      </Box>

      <Chip
        icon={<StarOutline sx={{ color: 'white !important' }} />}
        label="FONCTIONNALITÉ PREMIUM"
        sx={{
          bgcolor: '#F59E0B',
          color: 'white',
          fontWeight: 700,
          fontSize: '0.85rem',
          mb: 2,
        }}
      />

      <Typography variant="h6" sx={{ fontWeight: 600, color: '#92400E', mb: 1 }}>
        Passez à Premium pour débloquer {featureName}
      </Typography>

      <Typography variant="body1" sx={{ color: '#92400E', mb: 3 }}>
        Profitez de fonctionnalités avancées pour professionnaliser votre activité
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
          ✍️ Signature électronique à distance
        </Typography>
        <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
          💳 Paiement client par CB intégré
        </Typography>
        <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
          📊 Comptabilité automatisée
        </Typography>
      </Box>

      <Button
        variant="contained"
        size="large"
        onClick={() => navigate('/pricing')}
        sx={{
          mt: 3,
          py: 1.5,
          px: 4,
          fontSize: '1rem',
          fontWeight: 700,
          bgcolor: '#F59E0B',
          color: 'white',
          '&:hover': {
            bgcolor: '#D97706',
          },
        }}
      >
        Passer à Premium
      </Button>

      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#92400E' }}>
        À partir de 16,58€/mois (facturation annuelle)
      </Typography>
    </Paper>
  );
};

export default PremiumFeatureGuard;
